import React, { useEffect, useCallback, useState } from "react";
import { AllOperators } from "./constants";
import { Filter, TextFilter, NumericFilter, BooleanFilter } from "@progress/kendo-react-data-tools";
import { Button } from "@progress/kendo-react-buttons";
import { Utils } from "../../helpers";
import moment from "moment";
import DateFilter from "./dateFilter";
import "./index.scss";
const initialFilter = {
  logic: "and",
  filters: [],
};

export default function FilterBuilder({ filterConfig, blackListedOperators, selectedFilters, onFilterUpdate, appName, disableApplyBtn }) {
  const [filterBuilderConfig, setFilterBuilderConfig] = useState([]);
  const [filterData, setFilterData] = useState(initialFilter);
  const [errorMessage, setErrorMessage] = useState("");
  const customComponent = {
    date: useCallback((props) => <DateFilter onDateFilterChange={handleFilterChange} filter={props.filter} />, [DateFilter]),
  };

  useEffect(() => {
    if (filterConfig?.length > 0) {
      let filterBuilderConfig = [];
      filterConfig?.map((filter) => {
        const blackList = blackListedOperators[filter.type] || [];
        const allowedOperators = AllOperators[filter.type].filter((op) => !blackList.includes(op.operator));

        let obj = {
          ...filter,
          filter:
            filter.type === "date"
              ? customComponent["date"]
              : filter.type === "numeric"
              ? NumericFilter
              : filter.type === "boolean"
              ? BooleanFilter
              : TextFilter,
          operators: allowedOperators,
        };

        filterBuilderConfig.push(obj);
      });
      setFilterBuilderConfig(filterBuilderConfig);
    }
  }, [filterConfig]);

  useEffect(() => {
    if (selectedFilters) {
      if (selectedFilters?.filters?.length > 0) {
        const updatedFilters = assignUUIDToFilter(selectedFilters);
        setFilterData(updatedFilters);
      } else setFilterData(initialFilter);
    }
  }, []);

  useEffect(() => {
    // function to update buttons label
    setTimeout(() => {
      updateFilterBuilderLabels();
    }, 0);
  }, [filterData]);

  const updateFilterBuilderLabels = () => {
    const filterBuilderContainer = document.querySelector(`[data-id="${appName}_Window"]`)?.querySelector(".filter-builder-container");
    const filterToolbarContainer = filterBuilderContainer?.querySelector(".k-filter-container");

    if (filterToolbarContainer) {
      // Find all buttons in the filter toolbar
      const filterToolbarButtons = filterToolbarContainer.querySelectorAll("button");
      let removed = false;

      filterToolbarButtons.forEach((btn) => {
        if (btn.textContent.includes("Add Expression")) btn.innerHTML = `<i class="fa-solid fa-plus fa-icon"></i>Add Filter`;
        if (btn.textContent.includes("Add Group")) btn.innerHTML = `<i class="fa-solid fa-plus fa-icon"></i>Add Group`;

        // Hide only the first 'Remove' button
        if (!removed && btn.title === "Remove" && btn.tabIndex === -1) {
          btn.style.display = "none";
          removed = true;
        }
      });
    }
  };

  const updateFilterValueByUuid = (filters, uuid, newValue) => {
    return filters.map((filter) => {
      if (filter.uuid === uuid) {
        return { ...filter, value: newValue };
      }

      // If nested filters exist, recurse
      if (filter.filters) {
        return {
          ...filter,
          filters: updateFilterValueByUuid(filter.filters, uuid, newValue),
        };
      }

      return filter;
    });
  };

  const validateAndProcessFilter = (selectedFilter) => {
    let hasNull = false;
    
    //  check validation && format date objects to "YYYY-MM-DD" before save
    const cleanAndFormatFilter = (filterValue) => {
      if (!filterValue) return null;

      let filter = { ...filterValue };

      if (filter.operator === "isnull") {
        filter.value = null;
      }

      if (filter.operator !== "isnull" && (filter.value === null || filter.value === "")) {
        hasNull = true;
      }

      if ("uuid" in filter) {
        delete filter.uuid;
      }

      if (typeof filter.value === "object" && (filter.operator === "between" || filter.operator === "ytd" || filter.operator === "mtd")) {
        if (filter.value?.start === null || filter.value?.start === "" || filter.value?.end === null || filter.value?.end === "") {
          hasNull = true;
        } else {
          filter.value.start = moment(filter.value.start).format("YYYY-MM-DD");
          filter.value.end = moment(filter.value.end).format("YYYY-MM-DD");
        }
      } else if (filter.value instanceof Date) {
        filter.value = moment(filter.value).format("YYYY-MM-DD");
      }

      if (filter.filters && Array.isArray(filter.filters)) {
        filter.filters = filter.filters.map(cleanAndFormatFilter);
      }

      return filter;
    };

    const formattedFilter = cleanAndFormatFilter(selectedFilter);
    return { filter: formattedFilter, hasNull };
  };

  const onFilterChange = (filter) => {
    if (filter?.filters?.length > 0) {
      const { filter: formattedFilter, hasNull } = validateAndProcessFilter(filter);

      if (hasNull) {
        setErrorMessage("Please enter a value for the filter.");
        disableApplyBtn(true);
      } else {
        setErrorMessage(""); // Clear error if no nulls
        onFilterUpdate(formattedFilter);
        disableApplyBtn(false);
      }
    } else {
      setErrorMessage(""); // In case it's empty
      onFilterUpdate(filter);
      disableApplyBtn(false);
    }
  };

  const assignUUIDToFilter = (selectedFilters) => {
    // creating a deep copy of selectedFilters
    const filter = JSON.parse(JSON.stringify(selectedFilters));

    // add uuid to each filter
    if (!filter.uuid) {
      filter.uuid = Utils.generateUUID();
    }

    if (filter.filters && Array.isArray(filter.filters)) {
      filter.filters = filter.filters.map(assignUUIDToFilter);
    }
    return filter;
  };

  const handleFilterChange = (event) => {
    //for kendo builder changes
    if (event.filter) {
      const updatedFilter = assignUUIDToFilter(event.filter);
      setFilterData(updatedFilter);
      onFilterChange(updatedFilter);
    } else {
      //for custom changes to value from multiselect and date component
      setFilterData((prev) => {
        const updatedFilters = updateFilterValueByUuid(prev.filters, event.uuid, event.value);
        onFilterChange({
          ...prev,
          filters: updatedFilters,
        });
        return {
          ...prev,
          filters: updatedFilters,
        };
      });
    }
  };

  return (
    <div className="filter-builder">
      {filterBuilderConfig?.length > 0 ? (
        <>
          <div className="header">Filter Builder</div>
          <div className="sub-header">Create custom filters using flexible logic and conditions based on available columns in the table.</div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
          <div className="filter-container scroll-container">
            <div className="reset-btn-container">
              <Button
                fillMode={"outline"}
                title={`Reset`}
                className="k-grid-btn-outline reset-btn"
                onClick={() => {
                  setFilterData(initialFilter);
                  onFilterChange(initialFilter);
                }}
              >
                <i class="fa-solid fa-arrows-rotate fa-icon"></i>Reset
              </Button>
            </div>
            <Filter value={filterData} onChange={handleFilterChange} fields={filterBuilderConfig} />
          </div>
        </>
      ) : (
        <div className="sub-header">No filterable columns available. Please enable filtering for at least one column in the list configuration.</div>
      )}
    </div>
  );
}
